import React, { useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import Animated, { withTiming, interpolate, useSharedValue, interpolateColor, useAnimatedStyle, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import StatusBar from 'design-system/StatusBar';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import SearchBar from 'design-system/SearchBar';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView, Indicator, NavigationHeader } from 'design-system';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation, useMainStackRoute } from 'utils/types/navigationV6';
import NoSearchResults from './NoSearchResults';
import StockSearchResult from './StockSearchResult';
import useStockSearchResults from '../hooks/useStockSearchResults';
import StockSearchResultWatchList from './StockSearchResultWatchList';
import SetupInvestmentAccountButton from './SetupInvestmentAccountButton';
import { selectShowSetupInvestAccountInInvestScreens } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const paddingTop = rem(8);
const keyExtractor = (item) => item.id;
const SearchStocksScreen = () => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const route = useMainStackRoute();
    const [searchResults, search, , isFast, , loadMore] = useStockSearchResults(true, 'alpaca');
    const scrollY = useSharedValue(0);
    const searchBarRef = useRef(null);
    const showSetupInvestAccountInInvestScreens = useAppSelector(selectShowSetupInvestAccountInInvestScreens);
    useLayoutEffect(() => {
        const isWatchListFlow = route.params?.watchListId;
        navigation.setOptions({
            title: isWatchListFlow ? 'Add to List' : 'Stocks',
            headerTintColor: colors.text.primary,
        });
    }, [colors.background.light, colors.text.primary, route.params]);
    useAutoFocusInput(searchBarRef);
    const searchBarY = route.params?.searchBarY;
    const watchListId = route.params?.watchListId;
    const stockAlertFlow = route.params?.stockAlertFlow;
    const recurringOrderFlow = route.params?.recurringOrderFlow;
    const progressSharedValue = useSharedValue(searchBarY ? 0 : 1);
    useEffect(() => {
        if (searchBarY) {
            progressSharedValue.value = withTiming(1);
        }
    }, [progressSharedValue, searchBarY]);
    const renderItem = useCallback(({ item }) => watchListId ? (<StockSearchResultWatchList data={item} watchListId={watchListId}/>) : (<StockSearchResult data={item} recurringOrderFlow={recurringOrderFlow} stockAlertFlow={stockAlertFlow}/>), // WARNING: assuming user doesn’t add more than a few hundred stocks to a watchlist, because `{ ...item, watchlistId }` breaks memoization and slows down virtualized list performance dramatically
    [recurringOrderFlow, stockAlertFlow, watchListId]);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', showSetupInvestAccountInInvestScreens ? HEIGHT_PLUS_PADDING : 0, paddingHorizontalStyle, styles.content);
    const headerHeight = useDefaultHeaderHeight();
    const searchBarAnimatedStyle = useAnimatedStyle(() => {
        if (!searchBarY) {
            return {};
        }
        const topHeight = headerHeight + paddingTop;
        const offset = searchBarY - topHeight;
        return {
            transform: [
                {
                    translateY: interpolate(progressSharedValue.value, [0, 1], [offset, 0]),
                },
            ],
        };
    }, [headerHeight, progressSharedValue, searchBarY]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.flex}>
        <NavigationHeader scrollY={scrollY} withFrame>
          <Animated.View style={searchBarAnimatedStyle}>
            <SearchBar ref={searchBarRef} noDebounce={isFast} onChangeSearchTerm={search} textInputBgStyles={textInputStyle} containerStyle={styles.searchBar} placeholder={'Search stocks'}/>
          </Animated.View>
        </NavigationHeader>
        {searchResults ? (<AnimatedFlashList data={searchResults} renderItem={renderItem} onEndReached={loadMore} onScroll={scrollHandler} scrollEventThrottle={16} onEndReachedThreshold={0.8} keyExtractor={keyExtractor} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" ListEmptyComponent={NoSearchResults} contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>) : (<Indicator dark style={styles.indicator}/>)}
        {showSetupInvestAccountInInvestScreens ? (<KeyboardAvoidingContainerNew sticky withMarginHorizontal={false}>
            <SetupInvestmentAccountButton brand floating noMarginBottom/>
          </KeyboardAvoidingContainerNew>) : null}
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    flex: {
        flex: 1,
    },
    content: {
        paddingTop: rem(16),
    },
    indicator: {
        paddingBottom: rem(200),
    },
    searchBar: {
        paddingTop: rem(8),
    },
}));
export default SearchStocksScreen;
